/**
 * "Continue with Google" button styled to Google's sign-in branding rules:
 * official full-color G, the light/dark theme fill-stroke-text triples, 14/20
 * medium label, and per-platform padding around the G and label.
 * See https://developers.google.com/identity/branding-guidelines
 *
 * Deliberately not built on the shared `Button`: its outline variant pulls
 * fill, border and text from the app theme, which the brand rules forbid.
 * Height and radius still match the `lg` Button so it sits level with other
 * auth actions.
 *
 * Font: Google asks for Google Sans Medium, which isn't licensed for general
 * app use; the app's own medium face stands in at the specified size.
 */

import { Text } from '@/components/ui/text';
import { HANKEN_FAMILY } from '@/components/ui/text-weight';
import { useColorScheme } from 'nativewind';
import { ActivityIndicator, Platform, Pressable, StyleSheet } from 'react-native';
import { GoogleGLogo } from './google-g-logo';

export const GOOGLE_BUTTON_THEMES = {
  light: { fill: '#FFFFFF', stroke: '#747775', text: '#1F1F1F' },
  dark: { fill: '#131314', stroke: '#8E918F', text: '#E3E3E3' },
} as const;

export const GOOGLE_BUTTON_PADDING = {
  ios: { beforeLogo: 16, betweenLogoAndText: 12, afterText: 16 },
  default: { beforeLogo: 12, betweenLogoAndText: 10, afterText: 12 },
} as const;

/** Google's disabled state drops the button to 38% opacity. */
const DISABLED_OPACITY = 0.38;
const PRESSED_CLASS = 'active:opacity-80';
/** Matches the `lg` Button (h-14, rounded-lg) used by the other auth actions. */
const BUTTON_HEIGHT = 56;
const BUTTON_RADIUS = 8;

const padding = Platform.OS === 'ios' ? GOOGLE_BUTTON_PADDING.ios : GOOGLE_BUTTON_PADDING.default;

interface GoogleSignInButtonProps {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
  testID?: string;
}

export function GoogleSignInButton({
  label,
  onPress,
  disabled,
  loading,
  testID,
}: GoogleSignInButtonProps) {
  // NativeWind's scheme is the one actually rendered (the theme provider syncs it).
  const { colorScheme } = useColorScheme();
  const colors = colorScheme === 'light' ? GOOGLE_BUTTON_THEMES.light : GOOGLE_BUTTON_THEMES.dark;
  const inactive = !!(disabled || loading);

  return (
    <Pressable
      role="button"
      onPress={onPress}
      disabled={inactive}
      accessibilityLabel={label}
      accessibilityState={{ disabled: inactive, busy: !!loading }}
      testID={testID}
      // Must stay static: NativeWind's Pressable interop drops a
      // `style={({ pressed }) => ...}` function on native, which left the button
      // unstyled. Pressed feedback is an `active:` class instead.
      className={inactive ? undefined : PRESSED_CLASS}
      style={[
        styles.container,
        { backgroundColor: colors.fill, borderColor: colors.stroke },
        inactive && styles.disabled,
      ]}
    >
      {loading ? (
        <ActivityIndicator size="small" color={colors.text} />
      ) : (
        <>
          <GoogleGLogo />
          <Text style={[styles.label, { color: colors.text }]} numberOfLines={1}>
            {label}
          </Text>
        </>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: BUTTON_HEIGHT,
    borderRadius: BUTTON_RADIUS,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: padding.beforeLogo,
    paddingRight: padding.afterText,
    gap: padding.betweenLogoAndText,
  },
  label: {
    fontFamily: HANKEN_FAMILY.medium,
    fontSize: 14,
    lineHeight: 20,
  },
  disabled: { opacity: DISABLED_OPACITY },
});
